import { BaseClient } from '../base-client';
import type {
  CreateRatecardInput,
  ListRatecardsQuery,
  Ratecard,
  UpdateRatecardInput,
} from './types';

type One = { data: Ratecard };
type Maybe = { data: Ratecard | null };
type ListResponse = { data: Ratecard[]; meta: { total: number; limit: number; offset: number } };

export class RatecardsClient extends BaseClient {
  /** Paginated summary of the tenant's rate cards. */
  list(query: ListRatecardsQuery = {}): Promise<ListResponse> {
    return this.request('/api/ratecards', { query: normalize(query) });
  }

  /**
   * The single rate card in effect at `asOf` (defaults to today). Returns
   * `{ data: null }` when no card covers the date — the UI uses that as an
   * empty state, not a 404.
   */
  getActive(asOf?: string): Promise<Maybe> {
    return this.request('/api/ratecards/active', { query: asOf ? { asOf } : undefined });
  }

  get(id: string): Promise<One> {
    return this.request(`/api/ratecards/${encodeURIComponent(id)}`);
  }

  create(input: CreateRatecardInput): Promise<One> {
    return this.request('/api/ratecards', { method: 'POST', body: input });
  }

  update(id: string, input: UpdateRatecardInput): Promise<One> {
    return this.request(`/api/ratecards/${encodeURIComponent(id)}`, {
      method: 'PATCH',
      body: input,
    });
  }

  remove(id: string): Promise<One> {
    return this.request(`/api/ratecards/${encodeURIComponent(id)}`, { method: 'DELETE' });
  }
}

function normalize(q: object): Record<string, string | number | boolean | undefined> {
  const out: Record<string, string | number | boolean | undefined> = {};
  for (const [k, v] of Object.entries(q)) {
    if (v === undefined || v === '') continue;
    if (typeof v === 'string' || typeof v === 'number' || typeof v === 'boolean') {
      out[k] = v;
    }
  }
  return out;
}
